import { displayToast } from "../components/app/AppToast";
import { t } from "i18next";

export const getCurrentOS = (): "ios" | "android" | "web" => {
  const agent = window.navigator.userAgent;
  if (agent.includes("iPhone")) return "ios";
  if (agent.includes("Android")) return "android";
  return "web";
};

export function smoothScrollToTop(element: HTMLElement) {
  element.scrollTo({ top: 0, behavior: "smooth" });
}

export const pickGradient = (type: number) => {
  switch (type) {
    case 0: {
      return "linear-gradient(#FFD000, #FFA100)";
    }

    case 1: {
      return "linear-gradient(180deg, #FFD3A6 0%, #FF9C96 51.56%, #FC6F89 100%)";
    }

    case 2: {
      return "linear-gradient(180deg, #FFC408 0%, #FF734B 51.04%, #FF306E 100%)";
    }

    case 3: {
      return "linear-gradient(180deg, #FDB955 0%, #FF8A00 100%)";
    }

    case 4: {
      return "linear-gradient(180deg, #FF699C 0%, #FE5763 100%)";
    }

    default: {
      return "linear-gradient(180deg, #F42CAB 0%, #F1353A 100%)";
    }
  }
};

export const redirectToStore = () => {
  // MSStream is declared on Window in global.d.ts for IE detection
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    window.MSStream === undefined;
  const isAndroid =
    /Android/.test(navigator.userAgent) && window.MSStream === undefined;

  if (isIOS)
    window.location.href = "https://apps.apple.com/fr/app/juisci/id1588725828";
  else if (isAndroid)
    window.location.href = `https://play.google.com/store/apps/details?id=com.juisci.app&hl=us&gl=US`;
  else displayToast(t("Something went wrong. Please try again."), "error");
};

// NOTE: These specialties have been deemed to not have sufficient data yet
// and do not appear in the app for user. The list may change in the future.
export const UNAVAILABLE_SPECIALTIES = [
  "Correctional Health",
  "Cosmetic Dentistry", // Available in September
  "Endodontics", // Available in September
  "General Dentistry", // Available in September
  "Geriatric Dentistry", // Available in September
  "Implant Dentistry", // Available in September
  "Infusion Therapy",
  "Integrative Medicine", // Available in September
  "Non-Clinical",
  "Oral and Maxillofacial Pathology", // Available in September
  "Oral and Maxillofacial Radiology", // Available in September
  "Oral and Maxillofacial Surgery", // Available in September
  "Oral Microbiology", // Available in September
  "Orthodontics", // Available in September
  "Other Clinical",
  "Periodontics", // Available in September
  "Prosthodontics", // Available in September
  "Public Health Dentistry", // Available in September
  "Reconstructive Dentistry", // Available in September
  "School & College Health",
  "Speech-Language", // Available in September
  "Telemetry", // Available in September
];

/**
 * Specialties hidden everywhere in the webapp (filters, profile edit, home
 * scroller, onboarding, …) because they are non-medical / out-of-scope for the
 * current product scope. Matched by id (stable) rather than by translated name.
 */
export const HIDDEN_SPECIALTY_IDS = new Set([
  "6374e66cd7131b325440fad4", // Wound & Ostomy
  "637fd72ffd56f58b8f523383", // Case Management & Discharge Planning
  "637fd730fd56f58b8f523397", // Correctional Health
  "637fd73cfd56f58b8f5234c8", // Neurosurgery
  "637fd743fd56f58b8f523555", // Plastic Surgery
  "637fd745fd56f58b8f523591", // Quality Management
  "637fd747fd56f58b8f5235b9", // Risk Management & Utilization Review
  "637fd72efd56f58b8f52336f", // Aerospace Medicine
  "637fd736fd56f58b8f523437", // Home Care
  "637fd746fd56f58b8f5235a5", // Research
  "637fd751fd56f58b8f5236af", // Managed Care
  "637fd752fd56f58b8f5236d7", // Telemetry
  "637fd749fd56f58b8f5235e1", // Undersea & Hyperbaric Medicine
  "637fd74efd56f58b8f523673", // Acute Care
  "667148f36cb54cb4eccffb5c", // Medical Devices
  "637fd756fd56f58b8f523769", // Cosmetic Dentistry
  "637fd757fd56f58b8f5237f6", // Endodontics
  "637fd759fd56f58b8f523838", // Geriatric Dentistry
  "637fd75afd56f58b8f523852", // Implant Dentistry
  "637fd75cfd56f58b8f52387a", // Oral and Maxillofacial Pathology
  "637fd75dfd56f58b8f52388e", // Oral and Maxillofacial Surgery
  "637fd75bfd56f58b8f523866", // Oral Microbiology
  "637fd75efd56f58b8f5238a2", // Orthodontics
  "637fd75efd56f58b8f5238b6", // Periodontics
  "637fd741fd56f58b8f52352d", // Pharmacotherapy
  "637fd75ffd56f58b8f5238ca", // Prosthodontics
  "637fd760fd56f58b8f5238de", // Public Health Dentistry
  "637fd761fd56f58b8f5238f2", // Reconstructive Dentistry
  "637fd739fd56f58b8f523478", // Lab Medicine
]);

export function shortenText(str: string, max: number) {
  return str.length > max
    ? str.slice(0, Math.max(0, str.lastIndexOf(" ", max))) + "…"
    : str;
}

export function formatTime(timeInSeconds: number) {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
) {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>): Promise<ReturnType<T>> => {
    if (timer !== null) clearTimeout(timer);

    return new Promise((resolve, reject) => {
      timer = setTimeout(async () => {
        try {
          const result = await fn(...args);
          resolve(result as ReturnType<T>);
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      }, delay);
    });
  };
}
